/** Общего правила по регистру нет: при создании аудитора login_local и temp_password в snake_case */

import { readPage } from './pagination';
import { MmAdminMe, MmAuditor, MmAuditorMessenger, MmAuditorPasswordReset } from '../types/domain';
import { MmAuditorCreated } from '../types/dto';

function pickString(source: any, names: string[]): string | null {
  if (!source || typeof source !== 'object') {
    return null;
  }

  for (let i = 0; i < names.length; i += 1) {
    const value = source[names[i]];

    if (typeof value === 'string' && value !== '') {
      return value;
    }

    // Сервер может прислать число в текстовом поле — приводим к строке, чтобы не потерять
    if (typeof value === 'number' && isFinite(value)) {
      return String(value);
    }
  }

  return null;
}

/** Число не приводим к строке: строгое сравнение с числовыми идентификаторами перестанет совпадать */
function readId(raw: any): number | string {
  if (!raw || typeof raw !== 'object') {
    return 0;
  }

  const value = raw.id;

  if (typeof value === 'number' && isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value !== '') {
    return value;
  }

  return 0;
}

function readMessengers(raw: any): MmAuditorMessenger[] | null {
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.messengers)) {
    return null;
  }

  const result: MmAuditorMessenger[] = [];

  for (let i = 0; i < raw.messengers.length; i += 1) {
    const item = raw.messengers[i];

    if (item && typeof item === 'object' && typeof item.type === 'string') {
      result.push({
        type: item.type,
        username: typeof item.username === 'string' && item.username !== '' ? item.username : null,
      });
    }
  }

  return result;
}

/** Отсутствующие части ФИО читаем как null, а не пустой строкой: иначе форма сотрёт фамилию */
export function readAuditor(raw: any): MmAuditor {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 0,
      login: '',
      fullName: '',
      lastName: null,
      firstName: null,
      middleName: null,
      email: null,
      phone: null,
      region: null,
      messengers: null,
      tradingNetworkId: 0,
      createdAt: null,
    };
  }

  const tradingNetworkRaw = raw.tradingNetworkId !== undefined ? raw.tradingNetworkId : raw.trading_network_id;
  const tradingNetworkId = typeof tradingNetworkRaw === 'number' && isFinite(tradingNetworkRaw)
    ? tradingNetworkRaw
    : 0;

  /** Собираем в переменную: status и mustChangePassword обязаны отсутствовать, если сервер их не прислал */
  const auditor: MmAuditor = {
    id: readId(raw),
    // Логин одинаков в обеих формах; в бою это склейка `<код организации>.<loginLocal>`
    login: pickString(raw, ['login']) !== null ? (pickString(raw, ['login']) as string) : '',
    fullName: pickString(raw, ['fullName', 'fio']) !== null ? (pickString(raw, ['fullName', 'fio']) as string) : '',
    lastName: pickString(raw, ['lastName', 'last_name']),
    firstName: pickString(raw, ['firstName', 'first_name']),
    middleName: pickString(raw, ['middleName', 'middle_name']),
    email: pickString(raw, ['email']),
    phone: pickString(raw, ['phone']),
    region: pickString(raw, ['region']),
    messengers: readMessengers(raw),
    tradingNetworkId: tradingNetworkId,
    /** Точность createdAt различается между ответами: сравнивать записи по нему нельзя, только по id */
    createdAt: pickString(raw, ['createdAt', 'created_at']),
  };

  /** Две явные ветки вместо одного условия: иначе TS 3.4.1 не сузит string до литерального типа */
  const statusRaw = pickString(raw, ['status']);

  if (statusRaw === 'active') {
    auditor.status = 'active';
  } else if (statusRaw === 'deactivated') {
    auditor.status = 'deactivated';
  }

  const mustChangeRaw = raw.mustChangePassword !== undefined ? raw.mustChangePassword : raw.must_change_password;

  if (typeof mustChangeRaw === 'boolean') {
    auditor.mustChangePassword = mustChangeRaw;
  }

  return auditor;
}

export function readAuditors(rows: any): MmAuditor[] {
  if (!Array.isArray(rows)) {
    return [];
  }

  const result: MmAuditor[] = [];

  for (let i = 0; i < rows.length; i += 1) {
    result.push(readAuditor(rows[i]));
  }

  return result;
}

export function readAuditorOptions(raw: any): MmAuditor[] {
  /** Ветка массива обязана идти первой: readPage не найдёт в голом массиве страницу и вернёт пустую */
  if (Array.isArray(raw)) {
    return readAuditors(raw);
  }

  return readAuditors(readPage<any>(raw).content);
}

/** Разбор терпит оба написания: на проводе temp_password, у сброса пароля — tempPassword */
export function readAuditorCreated(raw: any): MmAuditorCreated {
  const body = raw && typeof raw === 'object' ? raw : {};
  const source = body.auditor && typeof body.auditor === 'object' ? body.auditor : body;
  /** Порядок имён значим, список не сокращать: стенды шлют пароль под разными именами */
  const password = pickString(body, ['tempPassword', 'temporaryPassword', 'temp_password']);

  const created = readAuditor(source) as MmAuditorCreated;

  created.temporaryPassword = password;

  return created;
}

export function readAuditorPasswordReset(raw: any): MmAuditorPasswordReset {
  const body = raw && typeof raw === 'object' ? raw : {};
  const password = pickString(body, ['tempPassword', 'temporaryPassword', 'temp_password']);

  return {
    tempPassword: password !== null ? password : '',
  };
}

export function readAdminMe(raw: any): MmAdminMe {
  const body = raw && typeof raw === 'object' ? raw : {};
  const profile = body.profile && typeof body.profile === 'object' ? body.profile : body;

  const spUsername = pickString(profile, ['spUsername']);
  const instanceKey = pickString(profile, ['instanceKey']);
  const role = pickString(profile, ['role']);
  const organizationName = pickString(profile.organization, ['name']);

  return {
    spUsername: spUsername !== null ? spUsername : '',
    instanceKey: instanceKey !== null ? instanceKey : '',
    role: role !== null ? role : '',
    organizationName: organizationName !== null ? organizationName : '',
    serverTime: pickString(body, ['serverTime']),
  };
}

/** Пустое приводим к `null`: строка из пробелов доходит до проверки e-mail и даёт `422` */
function emptyToNull(value: any): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();

    return trimmed === '' ? null : trimmed;
  }

  if (typeof value === 'number' && isFinite(value)) {
    return String(value);
  }

  return null;
}

/** Имена в теле создания разнородны: login_local через подчёркивание, fullName слитно */
export function buildCreateAuditorBody(payload: any): { [key: string]: any } {
  const source = payload && typeof payload === 'object' ? payload : {};

  const body: { [key: string]: any } = {
    login_local: typeof source.login === 'string' ? source.login : '',
    /** Значения этим трём ключам присваиваются ниже, после переноса полей: выражение здесь затрётся */
    phone: null,
    email: null,
    region: null,
  };

  const keys = Object.keys(source);

  for (let i = 0; i < keys.length; i += 1) {
    body[keys[i]] = source[keys[i]];
  }

  /* emptyToNull только для необязательных полей: пустая строка в них значит «не задано» */
  body.phone = emptyToNull(source.phone);
  body.email = emptyToNull(source.email);
  body.region = emptyToNull(source.region);

  /* Присваивание после цикла: иначе перенос ключей payload перезапишет fullName сырым значением */
  body.fullName = typeof source.fullName === 'string' ? source.fullName : '';

  return body;
}

export function buildUpdateAuditorBody(payload: any): { [key: string]: any } {
  const source = payload && typeof payload === 'object' ? payload : {};
  const body: { [key: string]: any } = {};

  const keys = Object.keys(source);

  for (let i = 0; i < keys.length; i += 1) {
    body[keys[i]] = source[keys[i]];
  }

  body.fullName = typeof source.fullName === 'string' ? source.fullName : '';
  body.phone = emptyToNull(source.phone);
  body.email = emptyToNull(source.email);
  body.region = emptyToNull(source.region);
  /** Любое не-массивное значение уходит как null и стирает мессенджеры на бэкенде */
  body.messengers = Array.isArray(source.messengers) ? source.messengers : null;

  return body;
}

/** Тело обязано быть объектом: axios не отправит null, а без тела бэкенд ответит 400 */
export function buildResetPasswordBody(tempPassword?: string | null): { [key: string]: any } {
  return {
    tempPassword: emptyToNull(tempPassword),
  };
}

export function buildDeactivateAuditorBody(): { [key: string]: any } {
  return {
    force: false,
  };
}

/** Три имени query заданы вручную в snake_case — к camelCase не приводить */
export function buildAuditorsQuery(pageParams: { [key: string]: any }): { [key: string]: any } {
  const params: { [key: string]: any } = {};
  const keys = Object.keys(pageParams ? pageParams : {});

  for (let i = 0; i < keys.length; i += 1) {
    params[keys[i]] = pageParams[keys[i]];
  }

  const page = Number(params.page);
  const size = Number(params.size);
  const safeSize = isFinite(size) && size > 0 ? Math.floor(size) : 0;
  const safePage = isFinite(page) && page > 0 ? Math.floor(page) : 0;

  if (safeSize > 0) {
    /** limit допустим только в 1..200, offset — не меньше нуля; выход за границы даёт 422, а не обрезку */
    params.limit = safeSize > 200 ? 200 : safeSize;
    params.offset = safePage * safeSize;
  }

  const sortBy = mapSortFieldToWire(params.sortBy);

  if (sortBy !== null) {
    params.sort_by = sortBy;
    params.sort_dir = params.sortDirection === 'desc' ? 'desc' : 'asc';
  }

  return params;
}

/** Неизвестное значение sort_by бэкенд отвергает с 422, поэтому шлём только заведённые здесь */
export const MM_AUDITOR_SORT_FIELDS: { [key: string]: string } = {
  /* На проводе значение fio, а не fullName */
  fullName: 'fio',
  login: 'login',
  createdAt: 'created_at',
  region: 'region',
  status: 'status',
  // email: сознательно отсутствует — бэкенд такого значения не понимает.
};

export function mapSortFieldToWire(field: any): string | null {
  if (typeof field !== 'string' || field === '') {
    return null;
  }

  const mapped = MM_AUDITOR_SORT_FIELDS[field];

  return mapped === undefined ? null : mapped;
}

/** Имя поля в ошибке валидации приходит в camelCase, даже когда тело запроса в snake_case */
const FIELD_NAME_FROM_WIRE: { [key: string]: string } = {
  login_local: 'login',
  loginLocal: 'login',
  login: 'login',
  fio: 'fullName',
  fullName: 'fullName',
  email: 'email',
  phone: 'phone',
};

export function normalizeFieldErrors(error: any): void {
  const response = error && error.response ? error.response : null;
  const body = response && response.data ? response.data : null;

  if (!body || typeof body !== 'object') {
    return;
  }

  const raw = body.fieldErrors;

  if (!Array.isArray(raw)) {
    return;
  }

  const dictionary: { [key: string]: string } = {};

  for (let i = 0; i < raw.length; i += 1) {
    const item = raw[i];

    if (!item || typeof item !== 'object') {
      continue;
    }

    const wireField = typeof item.field === 'string' ? item.field : '';
    const message = typeof item.message === 'string' ? item.message : '';

    if (wireField === '' || message === '') {
      continue;
    }

    const formField = FIELD_NAME_FROM_WIRE[wireField];

    if (formField !== undefined) {
      dictionary[formField] = message;
    }
  }

  body.fieldErrorsRaw = raw;
  body.fieldErrors = dictionary;
}
